/* ═══════════════════════════════════════════
   INTEGRATION API — per-shop carrier management
   Talks to /shipping/integrations/* endpoints.
   Feature-flagged: callers fall back gracefully on failure.
   ═══════════════════════════════════════════ */
import { http } from "../../../utils/config";

/* ── Simple in-memory cache (1-min TTL) ── */
let _cache = { key: null, data: null, ts: 0 };
const CACHE_TTL = 60_000; // 1 minute

function getCached(key) {
  if (_cache.key === key && Date.now() - _cache.ts < CACHE_TTL) {
    return _cache.data;
  }
  return null;
}

function setCache(key, data) {
  _cache = { key, data, ts: Date.now() };
}

/**
 * Invalidate the integration cache.
 * Call after enable/disable to force fresh fetch.
 */
export function invalidateIntegrationCache() {
  _cache = { key: null, data: null, ts: 0 };
}

/**
 * Fetch the static carrier catalog (no per-shop state).
 */
export async function fetchCatalog() {
  const res = await http.post("/shipping/catalog");
  return res.data?.data || [];
}

/**
 * Fetch per-shop integrations (catalog + state).
 * Cached for 1 minute keyed by idw.
 * Returns { integrations: [], can_manage: boolean }.
 */
export async function fetchIntegrations(idw) {
  const cacheKey = `integrations_${idw}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const res = await http.post("/shipping/integrations", { idw });
  const result = {
    integrations: res.data?.data || [],
    can_manage: res.data?.can_manage ?? false,
  };
  setCache(cacheKey, result);
  return result;
}

/**
 * Enable a carrier for the shop.
 * @param {number} idw
 * @param {string} code - carrier code
 * @param {object} opts - { credentials, is_live, is_mock, settings }
 */
export async function enableCarrier(idw, code, opts = {}) {
  const res = await http.post(`/shipping/integrations/${code}/enable`, {
    idw,
    ...opts,
  });
  invalidateIntegrationCache();
  return res.data?.data;
}

/**
 * Disable a carrier for the shop.
 * @param {number} idw
 * @param {string} code - carrier code
 * @param {number|null} integrationId - optional; when multiple accounts, specify which to disable
 */
export async function disableCarrier(idw, code, integrationId = null) {
  const payload = { idw };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.post(`/shipping/integrations/${code}/disable`, payload);
  invalidateIntegrationCache();
  return res.data?.data;
}

/**
 * Disconnect a carrier completely (status=disconnected).
 * @param {number} idw
 * @param {string} code - carrier code
 * @param {number|null} integrationId - optional; when multiple accounts, specify which to disconnect
 */
export async function disconnectCarrier(idw, code, integrationId = null) {
  const payload = { idw };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.post(`/shipping/integrations/${code}/disconnect`, payload);
  invalidateIntegrationCache();
  return res.data?.data;
}

/**
 * Bootstrap legacy carriers: auto-detect configured GHN/VTP/Grab/AHM/VNP
 * and create integration rows. Idempotent.
 * @param {number} idw
 * @param {object} authContext - per-carrier auth from FE cookies
 */
export async function bootstrapLegacy(idw, authContext = {}) {
  const res = await http.post("/shipping/integrations/bootstrap-legacy", {
    idw,
    auth_context: authContext,
  });
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Run a health check for a specific carrier.
 */
export async function checkCarrierHealth(idw, code) {
  const res = await http.post("/shipping/integrations/health", {
    idw,
    carrier: code,
  });
  return res.data?.data;
}

/**
 * Đăng ký tài khoản khách hàng Viettel Post (ownerRegister).
 * Gọi trước khi đăng nhập nếu user chưa có tài khoản VTP.
 * @param {{ email: string, phone: string, name: string, address: string, wards_id: number }}
 */
export async function registerVtpOwner(data) {
  const res = await http.post("/shipping/vtp/owner-register", data);
  return res.data?.data ?? res.data;
}

/**
 * Validate J&T credentials before saving.
 * @param {string} apiKey
 * @param {string} apiAccount
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function validateJntCredentials(apiKey, apiAccount) {
  const res = await http.post("/shipping/integrations/jnt/validate", {
    api_key: apiKey,
    api_account: apiAccount,
  });
  return res.data;
}

/**
 * Connect J&T for a shop: validate + enable with credentials.
 * @param {number} idw
 * @param {string} apiKey
 * @param {string} apiAccount
 */
export async function connectJnt(idw, apiKey, apiAccount) {
  const res = await http.post("/shipping/integrations/jnt/enable", {
    idw,
    credentials: { api_key: apiKey, api_account: apiAccount },
    is_live: true,
    is_mock: false,
  });
  invalidateIntegrationCache();
  return res.data;
}

/* ── System Carrier Connect (connect_type=system, e.g. Grab) ── */

/**
 * Connect a system carrier. Performs real API handshake server-side.
 * Returns { data: { verify: { access_token, expires_in } } } on success.
 * @param {number} idw
 * @param {string} carrierCode
 * @param {boolean} confirmAdmin
 */
export async function connectSystemCarrier(idw, carrierCode, confirmAdmin = false) {
  const headers = {};
  if (confirmAdmin) {
    headers["X-ADMIN-CONFIRM"] = "YES";
  }
  const res = await http.post(
    `/shipping/integrations/${carrierCode}/connect`,
    { idw, ...(confirmAdmin ? { confirm: "YES" } : {}) },
    { headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/* ── Generic Manual Credential Connect (all connect_type=manual carriers) ── */

/**
 * Validate manual credentials for any connect_type=manual carrier.
 * @param {string} carrierCode
 * @param {object} credentials - key/value pairs matching required_credentials
 */
export async function validateManualCredentials(carrierCode, credentials) {
  const res = await http.post(
    `/shipping/integrations/${carrierCode}/validate`,
    { credentials }
  );
  return res.data;
}

/**
 * Connect a manual carrier for a shop.
 * @param {number} idw
 * @param {string} carrierCode
 * @param {object} credentials
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 * @param {string|null} connectionKey - Optional. When adding a second+ account, send unique key (e.g. account_123) so backend creates new row.
 */
export async function connectManualCarrier(idw, carrierCode, credentials, confirmAdmin = false, connectionKey = null) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const payload = { idw, credentials, ...(confirmAdmin ? { confirm: 'YES' } : {}) };
  if (connectionKey) payload.connection_key = connectionKey;
  const res = await http.post(
    `/shipping/integrations/${carrierCode}/connect`,
    payload,
    { headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Update manual carrier credentials. Empty secret fields keep old values.
 * @param {number} idw
 * @param {string} carrierCode
 * @param {object} credentials
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
/**
 * @param {number} idw
 * @param {string} carrierCode
 * @param {object} credentials
 * @param {boolean} confirmAdmin
 * @param {number|null} integrationId - Optional. When multiple accounts, which integration to update.
 */
export async function updateManualCarrier(idw, carrierCode, credentials, confirmAdmin = false, integrationId = null) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const payload = { idw, credentials, ...(confirmAdmin ? { confirm: 'YES' } : {}) };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.post(
    `/shipping/integrations/${carrierCode}/update`,
    payload,
    { headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Suspend a manual carrier: keep credentials, disable.
 * @param {number} idw
 * @param {string} carrierCode
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
export async function suspendManualCarrier(idw, carrierCode, confirmAdmin = false) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const res = await http.post(
    `/shipping/integrations/${carrierCode}/suspend`,
    { idw, ...(confirmAdmin ? { confirm: 'YES' } : {}) },
    { headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Disconnect a manual carrier completely (self-service: shop disconnects own integration).
 * Uses DELETE endpoint so normal users can disconnect their own idw; POST is admin-only.
 * @param {number} idw
 * @param {string} carrierCode
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 * @param {number|null} integrationId - optional; when multiple accounts, specify which to disconnect
 */
export async function disconnectManualCarrier(idw, carrierCode, confirmAdmin = false, integrationId = null) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const payload = { idw, ...(confirmAdmin ? { confirm: 'YES' } : {}) };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.delete(
    `/shipping/integrations/${carrierCode}/disconnect`,
    { data: payload, headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/* ── GHN Shop Resolution ── */

/**
 * Fetch GHN shops for a given API token.
 * Used for multi-shop selection during GHN connection.
 * @param {string} apiToken - GHN API token
 * @returns {Promise<{success: boolean, data: {shops: Array}}>}
 */
export async function fetchGhnShops(apiToken) {
  const res = await http.post("/shipping/integrations/ghn/shops", {
    api_token: apiToken,
  });
  return res.data;
}

/* ── Tracking Timeline API ── */

/**
 * Fetch tracking timeline for a shipment.
 * @param {number} shipmentId
 * @returns {Promise<{current_status: string, events: Array}>}
 */
export async function fetchTrackingTimeline(shipmentId) {
  const res = await http.get(`/shipping/trackings?shipment_id=${shipmentId}`);
  return res.data?.data || { current_status: null, events: [] };
}

/* ── Health Check API ── */

/**
 * Fetch health status for integrations.
 * @param {number} idw
 * @returns {Promise<Array>} carrier health data
 */
export async function fetchIntegrationHealth(idw) {
  const res = await http.post("/shipping/integrations/health", { idw });
  return res.data?.data;
}

/* ── SPX Manual Credential Connect (Haravan/Sapo style) ── */

/**
 * Validate SPX credentials before saving.
 * @param {string} userId
 * @param {string} secretKey
 */
/**
 * Config for SPX create-account section: allowed + test vs production (for label/description).
 * Doc: có thể tạo tài khoản qua form, không bắt buộc vào spx.vn.
 * @returns {{ allow_create_account: boolean, is_test_env: boolean }}
 */
export async function getSpxCreateAccountConfig() {
  const res = await http.get("/shipping/integrations/spx/create-account-allowed");
  const data = res.data?.data || {};
  return {
    allow_create_account: data.allow_create_account !== false,
    is_test_env: data.is_test_env === true,
  };
}

/**
 * SPX API 3.1: create account (app_id/app_secret only). Returns user_id + user_secret.
 * Use on test env only; backend returns 403 in production.
 * @param {string} phone
 * @param {string} [email]
 * @returns {{ user_id: number, user_secret: string }}
 */
export async function createSpxAccount(phone, email = "") {
  const res = await http.post("/shipping/integrations/spx/create-account", {
    phone: String(phone).trim(),
    ...(email ? { email: String(email).trim() } : {}),
  });
  if (!res.data?.data?.user_id || !res.data?.data?.user_secret) {
    throw new Error(res.data?.message || "Create account returned no credentials");
  }
  return res.data.data;
}

export async function validateSpxCredentials(userId, secretKey) {
  const res = await http.post("/shipping/integrations/spx/validate", {
    user_id: userId,
    secret_key: secretKey,
  });
  return res.data;
}

/**
 * Connect SPX for a shop with manual credentials.
 * @param {number} idw
 * @param {string} userId
 * @param {string} secretKey
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
export async function connectSpx(idw, userId, secretKey, confirmAdmin = false, addAccount = false) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const payload = {
    idw,
    user_id: userId,
    secret_key: secretKey,
    ...(confirmAdmin ? { confirm: 'YES' } : {}),
  };
  if (addAccount) {
    payload.connection_key = `acc_${Date.now()}`;
  }
  const res = await http.post("/shipping/integrations/spx/connect", payload, { headers });
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Update SPX credentials. Empty secretKey keeps old value.
 * @param {number} idw
 * @param {string} userId
 * @param {string} secretKey
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
export async function updateSpx(idw, userId, secretKey, confirmAdmin = false) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const body = { idw, user_id: userId, ...(confirmAdmin ? { confirm: 'YES' } : {}) };
  if (secretKey) body.secret_key = secretKey;
  const res = await http.post("/shipping/integrations/spx/update", body, { headers });
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Suspend SPX: keep credentials, disable carrier.
 * @param {number} idw
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
export async function suspendSpx(idw, confirmAdmin = false) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const res = await http.post("/shipping/integrations/spx/suspend", 
    { idw, ...(confirmAdmin ? { confirm: 'YES' } : {}) },
    { headers }
  );
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Disconnect SPX completely.
 * @param {number} idw
 * @param {boolean} confirmAdmin - If true, sends X-ADMIN-CONFIRM header for super admin
 */
export async function disconnectSpx(idw, confirmAdmin = false) {
  const headers = {};
  if (confirmAdmin) {
    headers['X-ADMIN-CONFIRM'] = 'YES';
  }
  const res = await http.delete("/shipping/integrations/spx/disconnect", {
    data: { idw, ...(confirmAdmin ? { confirm: 'YES' } : {}) },
    headers,
  });
  invalidateIntegrationCache();
  return res.data;
}

/* ── Grab Pickup Settings (shop users) ── */

/**
 * Save Grab pickup sender settings for a shop.
 * Normal shop users can call this for their own idw.
 * @param {number} idw
 * @param {{ pickup_name?: string, pickup_phone?: string, pickup_address?: string }} pickupSettings
 */
/**
 * @param {object} pickupSettings - pickup_name, pickup_phone, pickup_address, pickup_latitude, pickup_longitude
 * @param {number|null} integrationId - Optional. When multiple Grab accounts, which integration to update.
 */
export async function updateGrabPickupSettings(pickupSettings, integrationId = null) {
  const payload = { ...pickupSettings };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.post("/shipping/integrations/grab/pickup-settings", payload);
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Save Ahamove pickup settings (sender + địa chỉ lấy hàng + tọa độ).
 * Same payload shape as Grab: pickup_name, pickup_phone, pickup_address, pickup_latitude, pickup_longitude.
 * @param {object} pickupSettings
 * @param {number|null} integrationId - Optional. When multiple Ahamove accounts, which integration to update.
 */
export async function updateAhamovePickupSettings(pickupSettings, integrationId = null) {
  const payload = { ...pickupSettings };
  if (integrationId != null && integrationId > 0) payload.integration_id = integrationId;
  const res = await http.post("/shipping/integrations/ahm/pickup-settings", payload);
  invalidateIntegrationCache();
  return res.data;
}

/**
 * Update SPX pickup settings (per-account warehouse selection).
 * @param {{ warehouse_id: number, integration_id: number, idw?: number }} payload
 */
export async function updateSpxPickupSettings(payload) {
  const res = await http.post("/shipping/integrations/spx/pickup-settings", payload);
  invalidateIntegrationCache();
  return res.data;
}

/**
 * SPX: List account-level vouchers (Open API 1.10 list_voucher).
 * Optional: pass integrationId when shop có nhiều tài khoản SPX.
 * @param {number|null} integrationId
 * @returns {Promise<Array>} vouchers
 */
export async function getSpxVouchers(integrationId = null) {
  const payload = {};
  if (integrationId != null && integrationId > 0) {
    payload.integration_id = integrationId;
  }
  const res = await http.post("/shipping/spx/vouchers", payload);
  return res.data?.data || [];
}

/* ── Admin Feature Flags (super.admin only) ── */

/**
 * GET /admin/shipping/feature-flags
 * Returns current global feature flags for shipping.
 */
export async function fetchFeatureFlags() {
  const res = await http.get("/admin/shipping/feature-flags");
  return res.data?.data || {};
}

/**
 * POST /admin/shipping/toggle-open-api
 * Toggle global visibility of open_api carriers.
 * @param {boolean} hide - true to hide, false to show
 */
export async function toggleOpenApi(hide) {
  const res = await http.post("/admin/shipping/toggle-open-api", {
    hide,
    confirm: "YES",
  });
  invalidateIntegrationCache();
  return res.data;
}

/**
 * POST /admin/shipping/toggle-hidden-carriers
 * Update the list of individually hidden carrier codes.
 * @param {string[]} carriers - carrier codes to hide (e.g. ["vnp"])
 */
export async function toggleHiddenCarriers(carriers) {
  const res = await http.post("/admin/shipping/toggle-hidden-carriers", {
    carriers,
    confirm: "YES",
  });
  invalidateIntegrationCache();
  return res.data;
}

